/**
 * What a buyer has paid, and what follows from it.
 *
 * Winnie's Vehicle Status Registry spec, section 4, asks for three things this
 * platform did not have:
 *
 *   payment_status(vin):
 *       if amount_received == 0: return Unpaid
 *       if amount_received < amount_due: return PartiallyPaid
 *       if amount_received >= amount_due: return FullyPaid
 *
 *   - a PartiallyPaid vehicle stays on the AR chase REGARDLESS of deal status,
 *     "since the chase criterion is payment status, not deal status"
 *   - "Moving to settled should require payment_status = FullyPaid - don't allow
 *     a vehicle to be marked Settled while a balance is still outstanding; that
 *     would silently drop a real receivable out of the chase list."
 *
 * Nothing stopped that. A car could be closed with money still owing and it left
 * the chase list on the way out - the exact failure the spec is written to
 * prevent, and the kind nobody notices because the row simply stops appearing.
 *
 * One file, no database, no React. Every screen and service that needs to know
 * whether a car is paid asks here, so the Sales (AR) table, the reminders ladder
 * and the status transition cannot come to different conclusions about the same
 * car - which is how the sold-status lists drifted, twice.
 */

/** Cents, not floats. 0.1 + 0.2 has no place near a receivable. */
const cents = (n: number | null | undefined): number =>
  Math.round((Number(n) || 0) * 100)

export type ArPaymentStatus = 'Unpaid' | 'PartiallyPaid' | 'FullyPaid'

export const AR_PAYMENT_STATUSES: ArPaymentStatus[] = ['Unpaid', 'PartiallyPaid', 'FullyPaid']

/** What a person reads. The only labels for these three anywhere. */
export const AR_PAYMENT_STATUS_LABEL: Record<ArPaymentStatus, string> = {
  Unpaid: 'Unpaid',
  PartiallyPaid: 'Part paid',
  FullyPaid: 'Paid in full',
}

/**
 * The spec's payment_status, to the letter.
 *
 * A sale with no amount due yet - no invoice raised - is Unpaid rather than
 * FullyPaid. Zero over zero is arithmetically "all of it", and reading that as
 * paid would settle a car nobody has been billed for.
 */
export function paymentStatusOf(
  amountDue: number | null | undefined,
  amountReceived: number | null | undefined,
): ArPaymentStatus {
  const due = cents(amountDue)
  const received = cents(amountReceived)
  if (received <= 0) return 'Unpaid'
  if (due <= 0) return 'PartiallyPaid'
  if (received < due) return 'PartiallyPaid'
  return 'FullyPaid'
}

/** Sale less what has been received, never below zero, to the cent. */
export function amountOutstanding(
  amountDue: number | null | undefined,
  amountReceived: number | null | undefined,
): number {
  return Math.max(0, cents(amountDue) - cents(amountReceived)) / 100
}

/**
 * Is this car still being chased?
 *
 * Section 4: "a vehicle can be sold_delivered and still show up in the chase list
 * if payment_status = PartiallyPaid, since the chase criterion is payment status,
 * not deal status" - so the deal status is deliberately NOT a parameter here.
 *
 * A vehicle reverted to deal_pending leaves the AR list because the list itself
 * is filtered to sold statuses, "no separate exclusion flag needed". That is a
 * different question from this one and it is answered somewhere else on purpose.
 */
export function onChaseList(status: ArPaymentStatus): boolean {
  return status !== 'FullyPaid'
}

export interface SettleGate {
  ok: boolean
  reason: string
}

/**
 * May this vehicle be marked Settled?
 *
 * Only when the money is all in. The reason is written for the person who just
 * clicked the button, because "not allowed" with no figure in it is the kind of
 * refusal people work around rather than act on.
 */
export function settleGate(
  amountDue: number | null | undefined,
  amountReceived: number | null | undefined,
): SettleGate {
  const status = paymentStatusOf(amountDue, amountReceived)
  if (status === 'FullyPaid') return { ok: true, reason: 'Paid in full.' }
  const owing = amountOutstanding(amountDue, amountReceived)
  const money = owing.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
  return {
    ok: false,
    reason:
      status === 'Unpaid'
        ? `Nothing has been received against this sale yet — ${money} is outstanding. Settling it now would drop a real receivable off the chase list.`
        : `${money} is still outstanding. Record the balance before settling, or leave it on the chase list.`,
  }
}
